import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statsApi, goalsApi, languagesApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Target, Calendar, TrendingUp, Zap, Trophy, 
  ChevronRight, Save, RotateCcw, Sparkles, Clock
} from 'lucide-react';

// Country code mapping for flags (language code -> country code)
const CODE_TO_COUNTRY = {
  'es': 'es', 'fr': 'fr', 'de': 'de', 'deu': 'de', 'pt': 'br', 'por': 'br',
  'it': 'it', 'zh': 'cn', 'ja': 'jp', 'ko': 'kr', 'ar': 'sa', 'ru': 'ru',
  'fa': 'ir', 'fas': 'ir', 'tr': 'tr', 'hi': 'in', 'nl': 'nl', 'pl': 'pl',
  'en': 'gb', 'th': 'th', 'vi': 'vn', 'sv': 'se', 'da': 'dk', 'no': 'no',
  'nor': 'no', 'fi': 'fi', 'el': 'gr', 'he': 'il', 'id': 'id', 'ms': 'my', 
  'cs': 'cz', 'hu': 'hu', 'ro': 'ro', 'uk': 'ua', 'bg': 'bg', 'hr': 'hr', 
  'sk': 'sk', 'sl': 'si', 'sr': 'rs', 'lt': 'lt', 'lv': 'lv', 'et': 'ee', 
  'ca': 'es', 'eu': 'es', 'gl': 'es', 'sw': 'ke', 'tl': 'ph', 'bn': 'bd', 
  'ta': 'in', 'te': 'in', 'mr': 'in', 'gu': 'in', 'kn': 'in', 'ml': 'in', 
  'pa': 'in', 'ur': 'pk', 'ne': 'np', 'si': 'lk', 'my': 'mm', 'km': 'kh', 
  'lo': 'la', 'am': 'et', 'yo': 'ng', 'ig': 'ng', 'ha': 'ng', 'zu': 'za', 
  'xh': 'za', 'af': 'za', 'ps': 'af', 'nv': 'us', 'is': 'is', 'ht': 'ht',
  'bs': 'ba', 'hy': 'am', 'ka': 'ge', 'kk': 'kz', 'mk': 'mk', 'sq': 'al',
};

const getCountryCode = (langCode) => {
  if (!langCode) return null;
  return CODE_TO_COUNTRY[langCode.toLowerCase()] || CODE_TO_COUNTRY[langCode] || null;
};

// Flag image component
const FlagImage = ({ langCode, className = "w-6 h-4" }) => {
  const countryCode = getCountryCode(langCode);
  if (!countryCode) return <span className="text-lg">🌐</span>;
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      alt={langCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
};

// Preset goals
const presetGoals = [
  { label: 'Beginner', words: 500, description: 'Basic conversational vocabulary' },
  { label: 'Intermediate', words: 2000, description: 'Comfortable reading simple texts' },
  { label: 'Advanced', words: 5000, description: 'Read news and literature' },
  { label: 'Fluent', words: 10000, description: 'Near-native vocabulary coverage' },
];

// Daily target presets
const dailyPresets = [
  { label: 'Casual', words: 10, minutes: 5 },
  { label: 'Regular', words: 20, minutes: 10 },
  { label: 'Committed', words: 32, minutes: 15 },
  { label: 'Intensive', words: 50, minutes: 25 },
];

export default function GoalSetter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data
  const [languageStats, setLanguageStats] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [insights, setInsights] = useState(null);
  
  // Goal form state
  const [goalData, setGoalData] = useState({
    target_words: 2000,
    daily_target: 32,
    target_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [langStatsRes, insightsRes] = await Promise.all([
        statsApi.getLanguageStats(),
        goalsApi.getInsights().catch(() => ({ data: null })),
      ]);
      
      setLanguageStats(langStatsRes.data);
      setInsights(insightsRes.data);
      
      // Select first language by default
      if (langStatsRes.data.length > 0) {
        const firstLang = langStatsRes.data[0].language;
        setSelectedLanguage(firstLang?.language_id);
        await loadLanguageGoal(firstLang?.language_id);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadLanguageGoal = async (languageId) => {
    try {
      const res = await goalsApi.getLanguageGoal(languageId);
      if (res.data) {
        setGoalData({
          target_words: res.data.target_words || 2000,
          daily_target: res.data.daily_target || 32,
          target_date: res.data.target_date || '',
        });
      }
    } catch (err) {
      console.error('Error loading goal:', err);
    }
  };

  const handleLanguageChange = async (langId) => {
    setSelectedLanguage(langId);
    await loadLanguageGoal(langId);
  };

  const handlePresetSelect = (preset) => {
    setGoalData(prev => ({
      ...prev,
      target_words: preset.words,
    }));
  };

  const handleDailyPresetSelect = (preset) => {
    setGoalData(prev => ({
      ...prev,
      daily_target: preset.words,
    }));
  };

  const calculateProjectedDate = () => {
    if (!insights || !goalData.daily_target) return null;
    
    const currentKnown = insights.total_known || 0;
    const remaining = Math.max(0, goalData.target_words - currentKnown);
    const daysNeeded = Math.ceil(remaining / goalData.daily_target);
    
    const projected = new Date();
    projected.setDate(projected.getDate() + daysNeeded);
    return projected.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleSave = async () => {
    if (!selectedLanguage) {
      toast.error('Please select a language');
      return;
    }
    
    setSaving(true);
    try {
      await goalsApi.setLanguageGoal(selectedLanguage, {
        language_id: selectedLanguage,
        target_words: goalData.target_words,
        daily_target: goalData.daily_target,
        target_date: goalData.target_date || null,
      });
      
      toast.success('Goal saved successfully!');
    } catch (err) {
      console.error('Error saving goal:', err);
      toast.error('Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <GoalSetterSkeleton />;
  }

  const selectedLangData = languageStats.find(ls => ls.language?.language_id === selectedLanguage);
  const currentKnown = selectedLangData?.known_count || 0;
  const progressPercent = goalData.target_words > 0 
    ? Math.min(100, (currentKnown / goalData.target_words) * 100) 
    : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="goal-setter-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <Target className="h-8 w-8 text-primary" />
          Goal Setter
        </h1>
        <p className="text-muted-foreground mt-1">
          Set your vocabulary targets and track your progress
        </p>
      </div>

      {/* Language Selector */}
      {languageStats.length > 0 ? (
        <Card data-testid="language-selector-card">
          <CardHeader>
            <CardTitle className="text-lg">Select Language</CardTitle>
            <CardDescription>Choose which language to set goals for</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {languageStats.map(ls => {
                const lang = ls.language;
                const isSelected = selectedLanguage === lang?.language_id;
                
                return (
                  <button
                    key={lang?.language_id}
                    onClick={() => handleLanguageChange(lang?.language_id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? 'border-primary bg-primary/10' 
                        : 'border-transparent bg-muted/50 hover:bg-muted'
                    }`}
                    data-testid={`lang-btn-${lang?.code}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <FlagImage langCode={lang?.code} className="w-8 h-6" />
                      <span className="font-medium">{lang?.name}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {ls.known_count || 0} known
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Import some texts first to set language goals
            </p>
            <Button className="mt-4" onClick={() => navigate('/import')}>
              Import Text
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedLanguage && (
        <>
          {/* Current Progress */}
          <Card data-testid="current-progress-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Current Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold">{currentKnown.toLocaleString()}</span>
                  <span className="text-muted-foreground">
                    / {goalData.target_words.toLocaleString()} words
                  </span>
                </div>
                <Progress value={progressPercent} className="h-3" />
                <div className="flex justify-between text-sm">
                  <span className="text-green-500 font-medium">
                    {progressPercent.toFixed(1)}% complete
                  </span>
                  <span className="text-muted-foreground">
                    {(goalData.target_words - currentKnown).toLocaleString()} remaining
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vocabulary Target */}
          <Card data-testid="vocabulary-target-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Vocabulary Target
              </CardTitle>
              <CardDescription>How many words do you want to learn?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Preset buttons */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {presetGoals.map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => handlePresetSelect(preset)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      goalData.target_words === preset.words
                        ? 'border-primary bg-primary/10'
                        : 'border-transparent bg-muted/50 hover:bg-muted'
                    }`}
                    data-testid={`preset-${preset.label.toLowerCase()}`}
                  >
                    <div className="font-semibold">{preset.label}</div>
                    <div className="text-2xl font-bold text-primary mt-1">
                      {preset.words.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {preset.description}
                    </div>
                  </button>
                ))}
              </div>

              {/* Custom input */}
              <div className="space-y-2">
                <Label>Or set a custom target</Label>
                <div className="flex gap-3">
                  <Input
                    type="number"
                    value={goalData.target_words}
                    onChange={(e) => setGoalData(prev => ({ 
                      ...prev, 
                      target_words: parseInt(e.target.value) || 0 
                    }))}
                    className="max-w-[200px]"
                    data-testid="custom-target-input"
                  />
                  <span className="flex items-center text-muted-foreground">words</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Target */}
          <Card data-testid="daily-target-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5 text-orange-500" />
                Daily Target
              </CardTitle>
              <CardDescription>How many words per day?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Daily presets */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {dailyPresets.map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => handleDailyPresetSelect(preset)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      goalData.daily_target === preset.words
                        ? 'border-primary bg-primary/10'
                        : 'border-transparent bg-muted/50 hover:bg-muted'
                    }`}
                    data-testid={`daily-${preset.label.toLowerCase()}`}
                  >
                    <div className="font-semibold">{preset.label}</div>
                    <div className="text-2xl font-bold text-orange-500 mt-1">
                      {preset.words}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      ~{preset.minutes} min/day
                    </div>
                  </button>
                ))}
              </div>

              {/* Slider */}
              <div className="space-y-4">
                <div className="flex justify-between">
                  <Label>Fine-tune your daily goal</Label>
                  <span className="font-semibold text-primary">
                    {goalData.daily_target} words/day
                  </span>
                </div>
                <Slider
                  value={[goalData.daily_target]}
                  onValueChange={([value]) => setGoalData(prev => ({ ...prev, daily_target: value }))}
                  min={5}
                  max={100}
                  step={5}
                  className="w-full"
                  data-testid="daily-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5 (Easy)</span>
                  <span>50 (Moderate)</span>
                  <span>100 (Intense)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Projection */}
          <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20" data-testid="projection-card">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Sparkles className="h-4 w-4" />
                    Projected completion
                  </div>
                  <div className="text-2xl font-bold text-green-500">
                    {calculateProjectedDate() || 'Set your goals to see projection'}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    At {goalData.daily_target} words per day
                  </div>
                </div>
                <Calendar className="h-12 w-12 text-green-500/50" />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setGoalData({ target_words: 2000, daily_target: 32, target_date: '' });
              }}
              data-testid="reset-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="min-w-[120px]"
              data-testid="save-goal-btn"
            >
              {saving ? (
                'Saving...'
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Goal
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function GoalSetterSkeleton() {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-4 w-64 mt-2" />
      </div>
      <Card>
        <CardContent className="py-6">
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="py-6">
          <Skeleton className="h-32" />
        </CardContent>
      </Card>
    </div>
  );
}
